import { Box, Button, Grid, IconButton, Modal, Stack, Typography } from '@mui/material';
import CloseModalIcon from '../../icons/RoundCloseIcon.svg';
import { InputField } from '../../components/InputController';
import { useEffect, useState } from 'react';
import { EmailRegex } from '../../utils/regex';
import useRegInviteConfigs, { SubjectDetailsInvitation } from '../../pmui/residents/hooks/useRegInviteConfigs';
import RegInviteModal from '../../pmui/residents/InviteResident';
import { useSelector } from 'react-redux';
import { selectAuthState } from '../sagas/auth/authSlice';
import { UserRegistrationProfileDetails } from './UserRegistrationModalManager';
import { InvitedProfileDto, Role, UserProfileDto } from '@inosio/payments-sdk';
import { SubjectDetailsPassReset, usePasswordResetConfigs } from '../../pmui/residents/hooks/usePasswordResetConfigs';
import ResetPasswordModal from '../../pmui/residents/ResetPassword';

interface CreateNewProfileUserModalProps {
  profileDetails: UserRegistrationProfileDetails;
  open: boolean;
  onClose: () => void;
  profileUsers: UserProfileDto[];
  profileInvitedUsers: InvitedProfileDto[];
}

/**
 * Takes inputs for new profile user and builds props for the invitation modal, no api calls are made in this component
 * @param props
 * @returns
 */
const CreateNewProfileUserModal = (props: CreateNewProfileUserModalProps) => {
  const [openResetPasswordModal, setOpenResetPasswordModal] = useState(false);
  const [subjDetailsPassReset, setSubjDetailsPassReset] = useState<SubjectDetailsPassReset>({
    ProfileUuid: props.profileDetails.profileUuid ?? '',
    UserUuid: '',
    Email: '',
    CreatedAt: '',
  });
  const [firstName, setFirstName] = useState('');
  const [firstNameError, setFirstNameError] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [openInvitationModal, setOpenInvitationModal] = useState(false);
  const [subjDetailsInvitation, setSubjDetailsInvitation] = useState<SubjectDetailsInvitation>({
    ProfileUuid: props.profileDetails.profileUuid ?? '',
    Email: '',
    FirstName: '',
    MiddleName: '',
    LastName: '',
    RoleType: Role.Resident, //just a fallback,
    UpdateTokenId: props.profileDetails.UpdateTokenId,
  });

  const { isSendingInvitationSuccess } = useSelector(selectAuthState);

  /**
   * Validates name and email fields and builds the subject details object that is to be passed as props to the invitation modal
   */
  const handleContinue = () => {
    setFirstNameError('');
    setLastNameError('');
    setEmailError('');

    if (!firstName) setFirstNameError('First name is required.');
    if (!lastName) setLastNameError('Last name is required.');
    if (!email || (email && !EmailRegex.test(email))) setEmailError('Email is invalid.');

    if (firstName && lastName && EmailRegex.test(email)) {
      /**
       * Algorithm:
       * - Find registered users or invited profiles that have matching first name, last name and emailÏ if any.
       * - if there is a registered user with the matching name and email then it's the password reset case
       * - if there is an invited profile with the matching name and email then it's the reinvite case
       * - Else its the new invitation flow
       * - Edge case: It is also possible that first and last name match and email is different, in that case the api is smart enough to update the email of aa
       *   (account_activation) record that has a matching name.
       */
      const profileRegisteredUserWithMatchingNameEmail = props.profileUsers.find(
        (profileUser) =>
          profileUser.FirstName?.toLocaleLowerCase() === firstName.toLocaleLowerCase() &&
          profileUser.LastName?.toLocaleLowerCase() === lastName.toLocaleLowerCase() &&
          ((!!profileUser.MiddleName && profileUser.MiddleName?.toLocaleLowerCase() === middleName.trim().toLocaleLowerCase()) || (!profileUser.MiddleName && !middleName.trim())) &&
          profileUser.UserEmail === email,
      );
      const profileInvitedUserWithMatchingNameEmail = props.profileInvitedUsers.find(
        (profileInvite) =>
          profileInvite.FirstName?.toLocaleLowerCase() === firstName.toLocaleLowerCase() &&
          profileInvite.LastName?.toLocaleLowerCase() === lastName.toLocaleLowerCase() &&
          ((!!profileInvite.MiddleName && profileInvite.MiddleName?.toLocaleLowerCase() === middleName.trim().toLocaleLowerCase()) || (!profileInvite.MiddleName && !middleName.trim())) &&
          profileInvite.InvitedEmail === email,
      );

      if (profileRegisteredUserWithMatchingNameEmail) {
        handleContinueProfileRegisteredUser(profileRegisteredUserWithMatchingNameEmail);
      } else if (profileInvitedUserWithMatchingNameEmail) {
        handleContinueProfileInvitedUser(profileInvitedUserWithMatchingNameEmail);
      } else {
        setOpenInvitationModal(true);
        const subjectDetailsInvitation: SubjectDetailsInvitation = {
          ProfileUuid: props.profileDetails.profileUuid ?? '',
          Email: email ?? '',
          FirstName: firstName ?? '',
          MiddleName: middleName ?? null,
          LastName: lastName ?? '',
          RoleType: props.profileDetails.profileRole,
          UpdateTokenId: props.profileDetails.UpdateTokenId,
        };
        setSubjDetailsInvitation(subjectDetailsInvitation);
      }
    }
  };

  const handleContinueProfileRegisteredUser = (profileUser: UserProfileDto) => {
    const subjectDetails: SubjectDetailsPassReset = {
      ProfileUuid: props.profileDetails.profileUuid,
      UserUuid: profileUser.UserUuid,
      Email: profileUser.UserEmail,
      CreatedAt: profileUser.RegistrationDate,
    };
    setSubjDetailsPassReset(subjectDetails);
    setOpenResetPasswordModal(true);
    props.onClose();
  };

  const handleContinueProfileInvitedUser = (profileInviteUser: InvitedProfileDto) => {
    const subjectDetails: SubjectDetailsInvitation = {
      ProfileUuid: props.profileDetails.profileUuid,
      Email: profileInviteUser.InvitedEmail || '',
      FirstName: profileInviteUser.FirstName,
      MiddleName: profileInviteUser.MiddleName,
      LastName: profileInviteUser.LastName,
      RoleType: props.profileDetails.profileRole,
      TokenId: profileInviteUser.TokenId,
    };
    setSubjDetailsInvitation(subjectDetails);
    setOpenInvitationModal(true);
    props.onClose();
  };

  const handleCloseModal = () => {
    setOpenInvitationModal(false);
  };

  const handleClosePasswordResetModal = () => {
    setOpenResetPasswordModal(false);
  };

  const regInviteModalConfigs = useRegInviteConfigs({ handleCloseModal, subjectDetails: { ...subjDetailsInvitation }, openModal: openInvitationModal });
  const passwordResetModalconfigs = usePasswordResetConfigs({ handleCloseModal: handleClosePasswordResetModal, subjectDetails: { ...subjDetailsPassReset }, openModal: openResetPasswordModal });

  const handleCloseNewUserModal = () => {
    setFirstNameError('');
    setLastNameError('');
    setEmailError('');
    setFirstName('');
    setLastName('');
    setEmail('');
    props.onClose();
  };

  useEffect(() => {
    if (isSendingInvitationSuccess) {
      handleCloseNewUserModal();
    }
  }, [isSendingInvitationSuccess]);

  useEffect(() => {
    setFirstName(props.profileDetails.profileIntegrationFirstName);
    setMiddleName(props.profileDetails.profileIntegrationMiddleName);
    setLastName(props.profileDetails.profileIntegrationLastName);
    setEmail(props.profileDetails.profileIntegrationEmail);
  }, [props.profileDetails.profileIntegrationFirstName, props.profileDetails.profileIntegrationLastName, props.profileDetails.profileIntegrationEmail]);

  return (
    <Box>
      <Modal open={props.open} onClose={handleCloseNewUserModal} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
        <Box
          sx={{
            position: 'absolute' as 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 520,
            background: '#FFFFFF',
            boxShadow: '0px 0px 40px rgba(0, 0, 0, 0.1)',
            borderRadius: '24px',
            p: 4,
            outline: 'none',
            minHeight: '523px',
          }}
        >
          <Stack direction="column" justifyContent="start" alignItems="space-between" spacing={1}>
            <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} sx={{ position: 'relative' }}>
              <Typography
                style={{
                  fontFamily: '"Work Sans",Sans serif',
                  fontStyle: 'normal',
                  fontWeight: 600,
                  fontSize: '18px',
                  lineHeight: '22px',
                  color: '#14142B',
                  margin: '0 !important',
                }}
              >
                Add New User
              </Typography>
              <IconButton
                edge="start"
                color="inherit"
                aria-label="Close modal"
                onClick={handleCloseNewUserModal}
                sx={{
                  position: 'absolute',
                  right: 0,
                }}
              >
                <img src={CloseModalIcon} alt="close" />
              </IconButton>
            </Stack>
            <Stack direction="row" justifyContent="center" alignItems="center" spacing={2}>
              <Box sx={{ mt: 4 }}>
                <Grid container spacing={1}>
                  <Grid item xs={3} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography sx={{ fontFamily: 'Work Sans' }}>First Name:</Typography>
                  </Grid>
                  <Grid item xs={9}>
                    <InputField name={'fistName'} value={firstName} isError={false} errors={firstNameError} touched={!!firstNameError} onChange={(e) => setFirstName(e.target.value)} />
                  </Grid>
                  <Grid item xs={3} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography sx={{ fontFamily: 'Work Sans' }}>Middle Name:</Typography>
                  </Grid>
                  <Grid item xs={9}>
                    <InputField name={'middleName'} value={middleName} isError={false} onChange={(e) => setMiddleName(e.target.value)} />
                  </Grid>
                  <Grid item xs={3} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography sx={{ fontFamily: 'Work Sans' }}>Last Name:</Typography>
                  </Grid>
                  <Grid item xs={9}>
                    <InputField name={'lastName'} value={lastName} isError={false} errors={lastNameError} touched={!!lastNameError} onChange={(e) => setLastName(e.target.value)} />
                  </Grid>
                  <Grid item xs={3} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography sx={{ fontFamily: 'Work Sans' }}>Email:</Typography>
                  </Grid>
                  <Grid item xs={9}>
                    <InputField name={'Email'} value={email} isError={false} errors={emailError} touched={!!emailError} onChange={(e) => setEmail(e.target.value)} />
                  </Grid>
                </Grid>
                <Box
                  style={{
                    position: 'absolute',
                    bottom: '20px',
                    left: '24px',
                    right: '24px',
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <Button
                    variant="contained"
                    style={{
                      fontFamily: "'Open Sans', sans-serif",
                      fontStyle: 'normal',
                      fontWeight: 700,
                      width: '187px',
                      marginLeft: '8px',
                      background: '#39A2DB',
                      borderRadius: '24px',
                      textTransform: 'capitalize',
                    }}
                    onClick={handleContinue}
                  >
                    continue
                  </Button>
                </Box>
              </Box>
            </Stack>
          </Stack>
        </Box>
      </Modal>
      {openInvitationModal ? <RegInviteModal {...regInviteModalConfigs} /> : <></>}
      {openResetPasswordModal ? <ResetPasswordModal {...passwordResetModalconfigs} /> : <></>}
    </Box>
  );
};

export default CreateNewProfileUserModal;
